import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { useMemo, type ReactNode } from 'react';
import {
  Auth0ProviderWithRouter,
  RequireAuth,
  RequireVerifiedEmail,
  useAuthTokenWiring,
} from '@my/platform/auth';
import { directoryApi } from '@my/directory-api';
import { ToastContainer, GoogleMapsProvider } from '@my/ui';
import { Building2 } from 'lucide-react';
import { RequireCapability } from '@/auth/RequireCapability';
import { getConfig } from '@/configuration/config';
import { AppShell } from '@/layout/AppShell';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { LocationListPage } from '@/features/locations/LocationListPage';
import { PartyDetailsPage } from '@/features/parties/PartyDetailsPage';
import { PartyListPage } from '@/features/parties/PartyListPage';
import { ImportPage } from '@/features/settings/import/ImportPage';
import { CargoDefinitionsPage } from '@/features/settings/configuration/CargoDefinitionsPage';
import { CentralConfigurationPage } from '@/features/settings/configuration/CentralConfigurationPage';
import { ProductsPage } from '@/features/settings/products/ProductsPage';
import { DocumentsPage } from '@/features/settings/documents/DocumentsPage';
import { WorkflowsPage } from '@/features/settings/workflows/WorkflowsPage';
import { TripCheckpointsPage } from '@/features/settings/trip-checkpoints/TripCheckpointsPage';
import { AuditLogPage } from '@/features/settings/audit/AuditLogPage';
import { DocumentBuilder } from '@/features/settings/documents/builder/DocumentBuilder';
import { OrganisationPage } from '@/features/settings/organisation/OrganisationPage';
import { ResourcesPage } from '@/features/settings/resources/ResourcesPage';
import { UsersAndAccessPage } from '@/features/settings/users-and-access/UsersAndAccessPage';
import { resolveBootLanguage } from '@/localisation';
import { Diagnostics } from '@/pages/Diagnostics';
import { NotFound } from '@/pages/NotFound';
import AnalyticsPage from './features/analytics/AnalyticsPage';
import { InvoicingPage } from './features/finance/InvoicingPage';
import OrderDetailsPage from './features/orders/OrderDetailsPage';
import RosterPage from './features/roster/RosterPage';
import WorkViewPage from './features/views/WorkViewPage';
import { useMapsConfig } from '@my/directory-api/hooks';

/**
 * Bridges Auth0 → axios. Has to live INSIDE Auth0Provider so the hook can read
 * its context. Renders nothing.
 */
function AuthTokenBridge() {
  useAuthTokenWiring();
  return null;
}

export function App() {
  const cfg = getConfig();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Auth0ProviderWithRouter
          settings={{
            domain: cfg.a0Domain,
            clientId: cfg.a0ClientId,
            audience: cfg.a0Audience,
            subscription: 'tms',
            extraAuthorizationParams: { ui_locales: resolveBootLanguage() },
          }}
        >
          <AuthTokenBridge />
          <Routes>
            {/* Authenticated app shell — every screen renders inside <AppShell />. */}
            <Route
              element={
                <RequireAuth brand={{ title: 'My TMS', icon: Building2 }}>
                  {/* Email must be confirmed before any screen renders —
                        self-signup users arrive unverified. */}
                  <RequireVerifiedEmail
                    brand={{ title: 'My TMS', icon: Building2 }}
                    onResend={() => directoryApi.resendMyVerificationEmail()}
                  >
                    <DirectoryMapsProvider>
                      <AppShell />
                    </DirectoryMapsProvider>
                  </RequireVerifiedEmail>
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              {/*
                  Settings tree — one capability PER PAGE rather than one role
                  set for the whole branch. That is what lets a Manager reach
                  Users & Access and Resources without also reaching Workflows;
                  the old role guard could only say "one of these roles" for
                  every page under it.

                  Each guard reads the same server-computed capability list the
                  sidebar does, so a page can never be reachable while its menu
                  item is hidden, or the reverse. Someone deep-linking is
                  bounced to /dashboard rather than seeing a half-rendered
                  page that 403s on every call.
                */}
              <Route element={<RequireCapability anyOf={['settings.organisation']} />}>
                <Route path="settings/organisation" element={<OrganisationPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.centralConfiguration']} />}>
                <Route path="settings/configuration" element={<CentralConfigurationPage />} />
                {/* Cargo Definitions used to be a card on Central Configuration
                      and keeps its capability. */}
                <Route path="settings/cargo-definitions" element={<CargoDefinitionsPage />} />
                {/* Products sits under Central Configuration and shares its
                      capability. The page says so itself when the organisation
                      has them switched off, rather than bouncing a deep link. */}
                <Route path="settings/products" element={<ProductsPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.resources']} />}>
                <Route path="settings/resources" element={<ResourcesPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.usersAndAccess']} />}>
                <Route path="settings/users-and-access" element={<UsersAndAccessPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.documents']} />}>
                {/* Document Builder — the list, then one route per template. */}
                <Route path="settings/documents" element={<DocumentsPage />} />
                <Route path="settings/documents/:templateId" element={<DocumentBuilder />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.workflows']} />}>
                {/* Workflow settings — one page per workflow kind. */}
                <Route path="settings/workflows" element={<WorkflowsPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.tripCheckpoints']} />}>
                {/* TMS Mobile: only while the organisation has the module, and
                      Owner/Admin only — Directory grants the capability then. */}
                {/* PROTOTYPE (mock data) while the model is agreed — the saved
                      Step-1 page is still in the folder for when it's rebuilt. */}
                <Route path="settings/trip-checkpoints" element={<TripCheckpointsPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.auditLog']} />}>
                {/* Everything that changed in the organisation, across both
                      services. The page narrows itself to the record types the
                      reader may already open. */}
                <Route path="settings/audit-log" element={<AuditLogPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['settings.import']} />}>
                {/* Bulk import (contacts + orders). */}
                <Route path="settings/import" element={<ImportPage />} />
                {/* Back-compat: the importer used to live at /import (under Contacts). */}
                <Route path="import" element={<Navigate to="/settings/import" replace />} />
              </Route>
              {/* Operational pages, each behind the capability that shows its
                    menu item — otherwise hiding a menu would be cosmetic and a
                    pasted link would still open the page. */}
              <Route element={<RequireCapability anyOf={['customers']} />}>
                <Route path="customers" element={<PartyListPage mode="Customer" />} />
                <Route path="customers/new" element={<PartyDetailsPage mode="Customer" />} />
                <Route path="customers/:id" element={<PartyDetailsPage mode="Customer" />} />
              </Route>
              <Route element={<RequireCapability anyOf={['suppliers']} />}>
                <Route path="suppliers" element={<PartyListPage mode="Supplier" />} />
                <Route path="suppliers/new" element={<PartyDetailsPage mode="Supplier" />} />
                <Route path="suppliers/:id" element={<PartyDetailsPage mode="Supplier" />} />
              </Route>
              <Route element={<RequireCapability anyOf={['locations']} />}>
                <Route path="locations" element={<LocationListPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['analytics']} />}>
                <Route path="analytics" element={<AnalyticsPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['orders']} />}>
                <Route path="orders" element={<WorkViewPage />} />
                <Route path="orders/new" element={<OrderDetailsPage />} />
                <Route path="orders/:id" element={<OrderDetailsPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['roster']} />}>
                <Route path="roster" element={<RosterPage />} />
              </Route>
              <Route element={<RequireCapability anyOf={['invoicing']} />}>
                <Route path="finance/invoicing" element={<InvoicingPage />} />
              </Route>
              <Route path="_diagnostics" element={<Diagnostics />} />
              {/* TODO Phase 5+: real screens for remaining Settings sections. */}
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Auth0ProviderWithRouter>
      </BrowserRouter>
      <ToastContainer />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

/**
 * Google Maps for the signed-in app, with the browser key from Directory
 * (`GET maps/config`) rather than the public config.json. Inside the
 * authenticated shell because the request needs the user's token.
 */
function DirectoryMapsProvider({ children }: { children: ReactNode }) {
  const maps = useMapsConfig();
  return (
    <GoogleMapsProvider apiKey={maps.data?.googleMapsApiKey} pending={maps.isPending}>
      {children}
    </GoogleMapsProvider>
  );
}
